import { z } from 'zod';

import {
  ZodGetEmployeeSavingsPortfolioQuery,
  ZodGetEmployeeSavingsPortfolioResponse,
} from '@Common/types/b2b/portfolio';
import {
  getB2bEmployeesPortfolioEndpoint,
  withNirOrNttRequiredRefine,
  ZodB2bEmployeeDocumentDetails,
  ZodB2bEmployeeDocumentsListResponse,
  ZodB2bEmployeesFilters,
  ZodGetB2bEmployeeDocumentsQuery,
  ZodGetB2bEmployeesOneResponseUnrefined,
  ZodGetEmployeeMatchingContributionSummary,
  ZodPostCreateB2bEmployeeRequestUnrefined,
  ZodPostCreateB2bEmployeeResponse,
} from '@Common/types/bo/b2b/employees';
import { ZodEmployeesBulkExportBody } from '@Common/types/bo/b2b/employees/export';
import { getB2bEmployeesLockedFundsIdEndpoint } from '@Common/types/bo/b2b/employees/locked_funds';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { ZodBoExportResponse } from '@Common/types/bo/exports';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodIDPathParam } from '@Common/types/request';
import { ZodSecureEmail } from '@Common/types/zod/zod-email';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';

export const ZodGetBoExternalEmployeesOneResponse = ZodGetB2bEmployeesOneResponseUnrefined.omit({
  has_activation_code: true,
  activation_code_expires_at: true,
  activation_code_is_external: true,
  account_closed_at: true,
  account_closed_reason: true,
  eckert_status: true,
  eckert_status_changed_at: true,
  last_activity_at: true,
});

export type GetBoExternalEmployeesOneResponse = z.TypeOf<
  typeof ZodGetBoExternalEmployeesOneResponse
>;

export const ZodGetBoExternalEmployeesResponse = z.array(
  ZodGetBoExternalEmployeesOneResponse.omit({
    email_state: true,
    date_of_decease: true,
    secondary_email: true,
    secondary_email_state: true,
    has_last_otp: true,
  }),
);

export type GetBoExternalEmployeesResponse = z.TypeOf<typeof ZodGetBoExternalEmployeesResponse>;

export type EmployeeListExternalRow = Omit<
  GetBoExternalEmployeesOneResponse,
  | 'digital_comms_preferences_id'
  | 'is_deceased'
  | 'date_of_decease'
  | 'email_state'
  | 'secondary_email'
  | 'secondary_email_state'
  | 'has_last_otp'
  | 'is_er_eligible'
  | 'contract_types'
>;

export const getBoExternalEmployeesSortColumns: (keyof EmployeeListExternalRow)[] = [
  'id',
  'company_id',
  'company_name',
  'user_id',
  'firstname',
  'lastname',
  'email',
  'phone_number',
  'status',
  'street',
  'postalcode',
  'city',
  'country',
  'language',
  'created_at',
  'updated_at',
  'csp_code',
  'contract_ids',
  'is_activated_by_company',
];

export const ZodGetBoExternalEmployeesQuery = buildZodBoListQueryParsed(
  ZodB2bEmployeesFilters,
  getBoExternalEmployeesSortColumns,
  { range: [0, 100] },
);

export type GetBoExternalEmployeesQuery = BoListQueryBase<
  z.TypeOf<typeof ZodB2bEmployeesFilters>,
  (typeof getBoExternalEmployeesSortColumns)[number]
>;

export const getBoExternalEmployeesEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetBoExternalEmployeesQuery,
  responseBody: ZodGetBoExternalEmployeesResponse,
});
export type GetBoExternalEmployeesEndpoint = GetEndpointType<typeof getBoExternalEmployeesEndpoint>;

export const postBoExternalEmployeesExportEndpoint = endpoint({
  method: 'POST',
  body: ZodEmployeesBulkExportBody,
  responseBody: ZodBoExportResponse,
});
export type PostBoExternalEmployeesExportEndpoint = GetEndpointType<
  typeof postBoExternalEmployeesExportEndpoint
>;

export const getBoExternalEmployeesIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetBoExternalEmployeesOneResponse,
});
export type GetBoExternalEmployeesIdEndpoint = GetEndpointType<
  typeof getBoExternalEmployeesIdEndpoint
>;

const ZodPostCreateBoExternalEmployeeRequestUnrefined =
  ZodPostCreateB2bEmployeeRequestUnrefined.omit({
    company_id: true,
  });

const ZodPostCreateBoExternalEmployeeRequest = withNirOrNttRequiredRefine(
  ZodPostCreateBoExternalEmployeeRequestUnrefined,
);

const ZodPutBoExternalEmployeeResponse = z.object({
  id: z.number().int(),
});

export const postBoExternalEmployeesEndpoint = endpoint({
  method: 'POST',
  body: ZodPostCreateBoExternalEmployeeRequest,
  responseBody: ZodPostCreateB2bEmployeeResponse,
});
export type PostBoExternalEmployeesEndpoint = GetEndpointType<
  typeof postBoExternalEmployeesEndpoint
>;

export const getBoExternalEmployeesPortfolioEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetEmployeeSavingsPortfolioQuery,
  responseBody: ZodGetEmployeeSavingsPortfolioResponse,
});
export type GetBoExternalEmployeesPortfolioEndpoint = GetEndpointType<
  typeof getB2bEmployeesPortfolioEndpoint
>;

export const getBoExternalEmployeeMatchingContributionsSummaryEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  // What the user received on their other employee accounts is deliberately not exposed to a
  // company: it would tell an employer where else their employee works
  responseBody: ZodGetEmployeeMatchingContributionSummary.omit({
    other_employments_matching_contributions: true,
  }),
});
export type GetBoExternalEmployeeMatchingContributionsSummaryEndpoint = GetEndpointType<
  typeof getBoExternalEmployeeMatchingContributionsSummaryEndpoint
>;

export const getBoExternalEmployeesDocumentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bEmployeeDocumentsQuery,
  responseBody: ZodB2bEmployeeDocumentsListResponse,
});
export type GetBoExternalEmployeesDocumentsEndpoint = GetEndpointType<
  typeof getBoExternalEmployeesDocumentsEndpoint
>;

export const getBoExternalEmployeeDocumentDetailsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodB2bEmployeeDocumentDetails,
});
export type GetBoExternalEmployeeDocumentDetailsEndpoint = GetEndpointType<
  typeof getBoExternalEmployeeDocumentDetailsEndpoint
>;

const ZodPutBoExternalEmployeeRequest = z.object({
  ...ZodPostCreateBoExternalEmployeeRequestUnrefined.omit({
    automatic_onboarding: true,
  }).shape,
  secondary_email: ZodSecureEmail.nullish(),
});

export const putBoExternalEmployeesIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutBoExternalEmployeeRequest,
  responseBody: ZodPutBoExternalEmployeeResponse,
});
export type PutBoExternalEmployeesIdEndpoint = GetEndpointType<
  typeof putBoExternalEmployeesIdEndpoint
>;

export const getBoExternalEmployeesLockedFundsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: getB2bEmployeesLockedFundsIdEndpoint.responseBody,
});
export type GetBoExternalEmployeesLockedFundsIdEndpoint = GetEndpointType<
  typeof getBoExternalEmployeesLockedFundsIdEndpoint
>;
